import {isSvipMember} from '../../util/svipMembership';
import { TopupPanel } from './TopupPanel';
import newLogo from '../../assets/pknin-logo.png';
import { enterMobileFullscreen } from '../../util/mobileFullscreen';
import { rememberUserCenter, clearUserCenterSession } from '../../util/userCenterSession';
import { DailyFreeItems } from './DailyFreeItems';
import { API_URL } from '../../util/networkEndpoints';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TopupLeaderboard } from './TopupLeaderboard';
import { createPortal } from 'react-dom';
import { postData } from '../../util/fetch';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { LoadState, setId, setLoadState, setServerId } from '../../slices/accountSlice';
import { switchState } from '../../slices/stateSlice';
import { SiteState } from '../../enums';
import './UserCenter.css';
import './PortalPolish.css';
import './UserCenterMobile.css';
import { isMobileGameDevice } from '../../hooks/useMobileGameViewport';
import { useUserCenterViewport } from '../../hooks/useUserCenterViewport';
import { GoldExchange } from './GoldExchange';
import { CharacterSettings } from './CharacterSettings';
import { NameColorPanel } from './NameColorPanel';

interface Profile {
  servers?: {id:number;name:string;open:boolean;message:string}[];
  username: string;
  character: { displayName: string; level: number; avatar: number; svip?:boolean;svipExpiresAt?:number; vipExpiresAt: number } | null;
  currency: { gold: number; giftCertificates: number };
  server: { id: number; name: string; open: boolean; message: string };
}
interface Product {
  iid: number;
  name: string;
  src: string;
  category: string;
  price: number;
  basePrice: number;
}
interface Mall {
  categories?: { id: string; name: string }[];
  enabled: boolean;
  canBuy: boolean;
  sequence: number;
  products: Product[];
  currency: Profile['currency'];
}
interface RankRow {
  village?: number;
  rank: number;
  accountId: number;
  displayName: string;
  level: number;
  avatar: number;
  value: number;
}
interface Rankings {
  villages?: { village: number; total: number; leader: RankRow | null }[];
  rows: RankRow[];
  self: RankRow | null;
  total: number;
}
const defaultCategories = [
  ['popular', 'ยอดนิยม'],
  ['food', 'อาหาร'],
  ['gem', 'เจม'],
  ['pet', 'สัตว์เลี้ยง'],
  ['vip', 'VIP'],
  ['other', 'อื่น ๆ'],
];
const rankingVillages: Record<number, string> = {111:'หมู่บ้านไฟ',211:'หมู่บ้านหมอก',311:'หมู่บ้านเมฆ',411:'หมู่บ้านทราย',511:'หมู่บ้านหิน'};
const asset = (path: string) => `${process.env.REACT_APP_CDN_PATH ?? 'assets/'}${path}`;
const number = (value: number = 0) => value.toLocaleString('th-TH');
function Icon({ name }: { name: string }) {
  const paths: Record<string, string> = {
    home: 'M3 10 12 3l9 7v10H3V10Z M9 20v-7h6v7',
    rank: 'M8 3h8v6a4 4 0 0 1-8 0V3Z M8 5H4v3a4 4 0 0 0 4 4 M16 5h4v3a4 4 0 0 1-4 4 M12 13v5 M8 21h8 M9 18h6',
    mall: 'M3 9h18l-2-6H5L3 9Z M5 9v12h14V9 M9 21v-7h6v7',
    arrow: 'M5 12h14 M13 6l6 6-6 6',
    refresh: 'M20 7a8 8 0 1 0 1 7 M20 2v5h-5',
    logout: 'M9 4H4v16h5 M9 12h12 M16 7l5 5-5 5',
    search: 'M21 21l-5-5 M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0',
    coin: 'M12 3 3 12l9 9 9-9-9-9Z M12 8l-4 4 4 4 4-4-4-4Z',
  };
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d={paths[name] ?? paths.home} />
    </svg>
  );
}
async function postWithDeadline(url: string, data: any, timeoutMs = 15000): Promise<any> {
  let timer: number | undefined;
  try {
    return await Promise.race([
      postData(url, data),
      new Promise((_, reject) => {
        timer = window.setTimeout(() => reject(new Error('เซิร์ฟเวอร์ไม่ตอบสนอง กรุณาลองอีกครั้ง')), timeoutMs);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
}

type UserCenterProps={ jwt:string;onClose?:()=>void;initialTab?:'home'|'topup'|'mall';inGame?:boolean };
export function UserCenter(props:UserCenterProps) {
  const gameServer=useAppSelector(state=>state.account.serverId);
  const [serverId,setServerId]=useState(()=>{if(props.inGame&&[1,2].includes(gameServer??0))return gameServer!;try{return sessionStorage.getItem('pknin:uc-server')==='2'?2:1;}catch{return 1;}});
  const selectServer=(id:number)=>{if(id!==1&&id!==2)return;try{sessionStorage.setItem('pknin:uc-server',String(id));}catch{}setServerId(id);};
  return <UserCenterContent key={serverId} {...props} serverId={serverId} onServerChange={selectServer}/>;
}
function UserCenterContent({jwt,onClose,initialTab='home',inGame=false,serverId,onServerChange}:UserCenterProps&{serverId:number;onServerChange:(id:number)=>void}) {
  const serverName=serverId===2?'พันธปักษา':'แผ่นดินใหญ่';
  const [mobile]=useState(isMobileGameDevice);
  useUserCenterViewport(mobile);
  useLayoutEffect(() => {
    document.documentElement.classList.add('uc-open');
    if(inGame)window.dispatchEvent(new Event('pknin:account-viewport'));
    return () => {
      document.documentElement.classList.remove('uc-open');
      if(inGame)window.dispatchEvent(new Event('pknin:account-viewport'));
    };
  }, [inGame]);
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState<'home' | 'rank' | 'mall' | 'topupRank' | 'wallet' | 'topup'>(initialTab);
  const [profile, setProfile] = useState<Profile | null>(null),
    [mall, setMall] = useState<Mall | null>(null);
  const [error, setError] = useState(''),
    [loading, setLoading] = useState(true),
    [entering, setEntering] = useState(false);
  const [metric, setMetric] = useState<'level' | 'arena'>('level'),
    [rankings, setRankings] = useState<Rankings | null>(null),
    [rankingLoading, setRankingLoading] = useState(false);
  const [rankingVillage, setRankingVillage] = useState(0);
  const [category, setCategory] = useState('popular'),
    [selected, setSelected] = useState<Product | null>(null),
    [quantity, setQuantity] = useState(1);
  const categories = mall?.categories?.map(({ id, name }) => [id, name]) ?? defaultCategories;
  const [busy, setBusy] = useState(false),
    [purchaseError, setPurchaseError] = useState(''),
    [message, setMessage] = useState('');
  const [search, setSearch] = useState(''),
    [sort, setSort] = useState('default'),
    [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const mounted = useRef(true),
    purchasing = useRef(false),
    entryPending = useRef(false);
  const purchase = useRef<{ requestId: string; iid: number; quantity: number; cost: number; sequence: number }>();
  const request = useCallback(
    async (path: string, data: any = {}) => {
      const result = await postWithDeadline(`${API_URL}/user-center/${path}`, { jwt, ...data, serverId }, path === 'topup/slip' ? 45000 : 15000);
      if (result.error) {
        const error = new Error(result.error) as Error & { server: boolean };
        error.server = true;
        throw error;
      }
      return result;
    },
    [jwt,serverId]
  );
  const refresh = useCallback(async () => {
    const [profile, mall] = await Promise.all([request('profile'), request('mall')]);
    if (mounted.current) {
      setProfile(profile);
      setMall(mall);
      const available = mall.categories?.map((row: { id: string }) => row.id) ?? defaultCategories.map(([id]) => id);
      setCategory((current) => (available.includes(current) ? current : available[0] ?? ''));
      setUpdatedAt(new Date());
      setSelected((current) => (current ? mall.products.find((product: Product) => product.iid === current.iid) ?? null : null));
    }
  }, [request]);
  useEffect(() => {
    mounted.current = true;
    refresh()
      .catch((e) => {
        if (mounted.current) setError(e.message || 'เชื่อมต่อไม่สำเร็จ');
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    const timer = window.setInterval(() => {
      if (!purchasing.current) refresh().catch(() => {});
    }, 30000);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [refresh]);
  useEffect(() => {
    if (tab !== 'rank' && tab !== 'home') return;
    let active = true;
    setRankingLoading(true);
    const load = () =>
      request('rankings', { metric: tab === 'home' ? 'level' : metric, ...(tab === 'rank' && rankingVillage ? {village: rankingVillage} : {}) })
        .then((data) => {
          if (active) setRankings(data);
        })
        .catch((e) => {
          if (active) setError(e.message);
        })
        .finally(() => {
          if (active) setRankingLoading(false);
        });
    load();
    const timer = window.setInterval(load, 30000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [tab, metric, rankingVillage, request]);

  async function enter(targetServerId:number=serverId) {
    if (entryPending.current || !profile?.servers?.find(row=>row.id===targetServerId)?.open && !(targetServerId===serverId&&profile?.server.open)) return;
    // Request while the tap still has browser user activation, before network work.
    void enterMobileFullscreen();
    entryPending.current = true;
    setEntering(true);
    setError('');
    try {
      const result = await postWithDeadline(`${API_URL}/selectServer`, { jwt, server: targetServerId });
      if (!mounted.current) return;
      if (result.error || !result.jwt) throw Error(result.error || 'ไม่สามารถเข้าเซิร์ฟเวอร์ได้');
      localStorage.setItem('jwt', result.jwt);
      rememberUserCenter(jwt);
      dispatch(setServerId(targetServerId));
      dispatch(switchState(SiteState.GAME_LOADER));
    } catch (e) {
      if (mounted.current) {
        setError((e as Error).message);
        setEntering(false);
      }
      entryPending.current = false;
    }
  }
  function logout() {
    clearUserCenterSession();
    localStorage.removeItem('jwt');
    dispatch(setId(undefined));
    dispatch(setLoadState(LoadState.NOT_LOADED));
    onClose?.();
  }
  function choose(product: Product) {
    purchase.current = undefined;
    setSelected(product);
    setQuantity(1);
    setPurchaseError('');
    setMessage('');
  }
  async function buy() {
    if (!selected || !mall || purchasing.current) return;
    purchasing.current = true;
    setBusy(true);
    setPurchaseError('');
    purchase.current ??= {
      requestId: `${Date.now()}-${Math.random()}`,
      iid: selected.iid,
      quantity,
      cost: selected.price * quantity,
      sequence: mall.sequence,
    };
    try {
      await request('buy', { purchase: purchase.current });
      if (!mounted.current) return;
      purchase.current = undefined;
      setSelected(null);
      setMessage(`ซื้อ ${selected.name} ×${quantity} สำเร็จ ส่งเข้ากระเป๋าเซิร์ฟเวอร์ ${serverId} แล้ว`);
      await refresh();
    } catch (e) {
      if (mounted.current) {
        setPurchaseError((e as Error).message || 'เชื่อมต่อไม่สำเร็จ');
        if ((e as any).server) {
          purchase.current = undefined;
          await refresh().catch(() => {});
        }
      }
    } finally {
      purchasing.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const character = profile?.character;
  const products = (
    mall?.products.filter(
      (product) => product.category === category && `${product.name} ${product.iid}`.toLowerCase().includes(search.trim().toLowerCase())
    ) ?? []
  ).sort((a, b) =>
    sort === 'low' ? a.price - b.price : sort === 'high' ? b.price - a.price : sort === 'name' ? a.name.localeCompare(b.name, 'th') : 0
  );
  const svip=character?isSvipMember(character):false;
  const vip = svip || (character?.vipExpiresAt ?? 0) > Date.now();
  const total = purchase.current?.cost ?? (selected?.price ?? 0) * quantity;
  const affordable = (profile?.currency.gold ?? 0) >= total;
  return createPortal(
    <div className={`uc-page${mobile?' uc-mobile':''}`} style={inGame?{zIndex:40000}:undefined}>
      <header className="uc-header">
        <a
          className="uc-brand"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            setTab('home');
          }}>
          <img className="uc-brand-logo" src={newLogo} alt="PkNiN" />
          <div>
            PkNiN<small>PLAYER PORTAL</small>
          </div>
        </a>
        <div className="uc-account">
          <label className="uc-server-picker">เซิร์ฟเวอร์ <select aria-label="เลือกเซิร์ฟเวอร์ศูนย์บัญชี" value={serverId} disabled={busy||entering} onChange={event=>onServerChange(Number(event.target.value))}><option value={1}>[S01] แผ่นดินใหญ่</option><option value={2}>[S02] พันธปักษา</option></select></label>
          {inGame && <button type="button" className="uc-logout" onClick={onClose}>กลับเข้าเกม</button>}
          <span className="uc-account-greeting">
            ยินดีต้อนรับ, <b>{profile?.username ?? 'นินจา'}</b>
          </span>
          <button
            className={loading ? 'uc-refresh is-loading' : 'uc-refresh'}
            aria-label="รีเฟรชข้อมูล"
            title="รีเฟรชข้อมูล"
            disabled={loading || busy}
            onClick={() => {
              setLoading(true);
              refresh()
                .catch((e) => setError(e.message))
                .finally(() => setLoading(false));
            }}>
            <Icon name="refresh" />
          </button>
          <button className="uc-logout" onClick={logout}>
            <Icon name="logout" />
            <span>ออกจากระบบ</span>
          </button>
        </div>
      </header>
      <main className="uc-main">
        <div className="uc-page-title">
          <div>
            <span className="uc-eyebrow">
              PLAYER DASHBOARD / {tab === 'topup' ? 'TOP UP' : tab === 'home' ? 'OVERVIEW' : tab === 'rank' ? 'LEADERBOARD' : tab === 'topupRank' ? 'TOP SUPPORTERS' : tab === 'wallet' ? 'MY WALLET' : 'ITEM MALL'}
            </span>
            <h1>{tab === 'topup' ? 'เติมเงินอัตโนมัติ' : tab === 'home' ? 'ศูนย์ผู้เล่น' : tab === 'rank' ? 'จัดอันดับผู้เล่น' : tab === 'topupRank' ? 'อันดับยอดเติมสะสม' : tab === 'wallet' ? 'จัดการข้อมูลตัวละคร' : 'ไอเท็มมอลล์'}</h1>
            <p>
              {tab === 'topup' ? 'เลือกแพ็กเกจ โอนพร้อมเพย์ แล้วส่งสลิปเพื่อรับโกลด์และคูปอง' : tab === 'wallet' ? 'เปลี่ยนชื่อ เปลี่ยนรหัสผ่าน และตั้งค่าอีเมลของคุณ' : tab === 'home'
                ? 'ทุกการผจญภัย เริ่มต้นจากที่นี่'
                : tab === 'topupRank' ? 'ผู้สนับสนุนเกม เรียงตามยอดเติมสะสมเป็นบาท' : tab === 'rank'
                ? 'ก้าวสู่ทำเนียบยอดฝีมือแห่งแผ่นดินใหญ่'
                : 'เตรียมไอเท็มให้พร้อม แล้วออกไปสร้างตำนาน'}
            </p>
          </div>
          <div className="uc-title-status">
            <span className="uc-server-badge">
              <i className={profile?.server.open ? 'online' : ''} />
              เซิร์ฟเวอร์ {serverId} · {serverName}
            </span>
            <small>
              {updatedAt
                ? `อัปเดต ${updatedAt.toLocaleTimeString('th-TH', { timeZone: 'Asia/Bangkok', hour: '2-digit', minute: '2-digit' })} น.`
                : 'กำลังเชื่อมต่อ…'}
            </small>
          </div>
        </div>
        <nav className="uc-nav" aria-label="UserCenter">
          <div className="uc-nav-profile">
            <div className="uc-nav-avatar">
              <img src={asset(`icons/people/${character?.avatar ?? 1}.png`)} alt="" />
            </div>
            <strong>{character?.displayName ?? profile?.username ?? 'นินจาคนใหม่'}</strong>
            <small>{character ? `LEVEL ${character.level}` : 'เริ่มต้นเส้นทางนินจาของคุณ'}</small>
            {vip && <span className="uc-vip-badge">VIP MEMBER</span>}
          </div>
          <span className="uc-nav-caption">พื้นที่ของคุณ</span>
          {(
            [
              ['home', 'ศูนย์ผู้เล่น'],
              ['topup', 'เติมเงินอัตโนมัติ'],
              ['rank', 'จัดอันดับผู้เล่น'],
              ['topupRank', 'อันดับยอดเติมสะสม'],
              ['wallet', 'จัดการข้อมูลตัวละคร'],
              ['mall', 'ไอเท็มมอลล์'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              aria-label={label}
              aria-current={tab === id ? 'page' : undefined}
              className={tab === id ? 'active' : ''}
              onClick={() => {
                setError('');
                setTab(id);
              }}>
              <Icon name={id === 'topupRank' ? 'rank' : (id === 'wallet' || id === 'topup') ? 'coin' : id} />
              <span>{mobile ? ({home:'หน้าหลัก',topup:'เติมเงิน',rank:'อันดับ',topupRank:'ยอดสะสม',wallet:'บัญชี',mall:'ร้านค้า'} as const)[id] : label}</span>
              <span className="uc-nav-chevron" aria-hidden="true">
                ›
              </span>
            </button>
          ))}
          <div className="uc-nav-server">
            <div>
              <i className={profile?.server.open ? 'online' : ''} />
              {serverName}
            </div>
            <p>{profile?.server.open ? 'พร้อมสำหรับการผจญภัย' : 'ปิดปรับปรุงชั่วคราว'}</p>
            <small>เซิร์ฟเวอร์ 01 · PkNiN</small>
          </div>
        </nav>
        {error && (
          <div className="uc-alert" role="alert">
            {error}
            <button
              onClick={() => {
                setError('');
                refresh().catch((e) => setError(e.message));
              }}>
              ลองอีกครั้ง
            </button>
          </div>
        )}
        <div className="uc-wallet">
          <div className="uc-wallet-character">
            <span>ตัวละครของคุณ</span>
            <strong>{character?.displayName ?? 'ยังไม่ได้สร้างตัวละคร'}</strong>
            <small>{character ? 'นินจาแห่งแผ่นดินใหญ่' : 'เข้าเกมเพื่อสร้างตัวละคร'}</small>
          </div>
          <div>
            <span>เลเวลปัจจุบัน</span>
            <strong>
              <em>LV.</em> {character ? number(character.level) : '—'}
            </strong>
            <small>เติบโตไปในทุกการผจญภัย</small>
          </div>
          <div>
            <span>โกลด์ของคุณ</span>
            <strong className="uc-gold">
              <Icon name="coin" />
              {number(profile?.currency.gold)}
            </strong>
            <small>สำหรับซื้อไอเท็ม</small>
          </div>
          <div>
            <span>คูปองของคุณ</span>
            <strong className="uc-coupons">▣ {number(profile?.currency.giftCertificates)}</strong>
            <small>ใช้ได้ที่ร้านค้านินจาลับ</small>
          </div>
        </div>
        {tab === 'topup' && <section className="uc-section"><p className="uc-topup-target">ปลายทางเติมเงิน: เซิร์ฟเวอร์ {serverId} · {serverName}</p><TopupPanel request={request} onUpdated={refresh} canTopup={!loading && !!character}/></section>}
        {tab === 'wallet' && <><NameColorPanel request={request} onUpdated={refresh}/><CharacterSettings request={request} displayName={character?.displayName} onRenamed={refresh} onPasswordChanged={logout} /><section className="uc-section uc-wallet-tools"><div className="uc-section-heading"><div><span className="uc-eyebrow">MY WALLET</span><h2>แลกโกลด์เป็นคูปอง</h2></div></div>
        <GoldExchange currency={profile?.currency ?? { gold: 0, giftCertificates: 0 }} enabled={!loading && !!character}
          request={request} onExchanged={currency => {
            setProfile(current => current ? { ...current, currency } : current);
            setMall(current => current ? { ...current, currency } : current);
          }} /></section></>}
        {tab === 'home' && (
          <>
            <DailyFreeItems request={request}/>
            <section className="uc-hero">
              <div className="uc-hero-scenery" aria-hidden="true" />
              <div className="uc-hero-copy">
                <span className="uc-hero-label">
                  <i />
                  YOUR NEXT ADVENTURE
                </span>
                <h2>
                  โลกแห่งนินจา
                  <br />
                  กำลังรอ<span>คุณ</span>
                </h2>
                <p>
                  กลับสู่แผ่นดินใหญ่ พบเพื่อนร่วมทาง
                  <br />
                  และสร้างตำนานบทใหม่ในแบบของคุณ
                </p>
                <div className="uc-hero-entry">
                  {[1,2].map(id => {
                    const open=profile?.servers?.find(row=>row.id===id)?.open ?? (profile?.server.id===id&&profile.server.open);
                    return <button key={id} className="uc-primary uc-play" disabled={loading || entering || !open} onClick={()=>enter(id)}>
                      {entering ? 'กำลังเข้าสู่เกม…' : `เข้าเกม · เซิร์ฟเวอร์ ${id}`}
                      <Icon name="arrow" />
                    </button>;
                  })}
                </div>
                <small>
                  {loading ? 'กำลังโหลดข้อมูลบัญชี…' : profile?.server.open ? `${serverName} พร้อมให้บริการ` : profile?.server.message}
                </small>
              </div>
              <div className="uc-hero-art">
                <img src={asset('site/branding/naruto-sasuke-rivals.png')} alt="Naruto และ Sasuke" />
                <span>PkNiN · THE NINJA LEGACY</span>
              </div>
            </section>
            <div className="uc-feature-grid">
              <button onClick={() => setTab('topupRank')}><span className="uc-feature-icon gold"><Icon name="rank" /></span><div><small>TOP SUPPORTERS</small><h3>อันดับยอดเติมสะสม</h3><p>ทำเนียบตัวละครผู้สนับสนุน และยอดสะสมของคุณ</p></div><Icon name="arrow" /></button>
              <button onClick={() => setTab('wallet')}><span className="uc-feature-icon"><Icon name="home" /></span><div><small>CHARACTER & ACCOUNT</small><h3>จัดการข้อมูลตัวละคร</h3><p>เปลี่ยนชื่อ รหัสผ่าน และตั้งค่าอีเมลของบัญชี</p></div><Icon name="arrow" /></button>
              <button onClick={() => setTab('rank')}>
                <span className="uc-feature-icon">
                  <Icon name="rank" />
                </span>
                <div>
                  <small>HALL OF FAME</small>
                  <h3>ทำเนียบนินจา</h3>
                  <p>พบยอดฝีมือ ดูอันดับของคุณ</p>
                </div>
                <Icon name="arrow" />
              </button>
              <button onClick={() => setTab('mall')}>
                <span className="uc-feature-icon gold">
                  <Icon name="mall" />
                </span>
                <div>
                  <small>GEAR UP FOR ADVENTURE</small>
                  <h3>ไอเท็มมอลล์</h3>
                  <p>เลือกซื้อไอเท็ม ส่งตรงเข้ากระเป๋า</p>
                </div>
                <Icon name="arrow" />
              </button>
            </div>
            <section className="uc-home-bottom">
              <div className="uc-home-leaders">
                <div className="uc-card-heading">
                  <div>
                    <span className="uc-eyebrow">THE TOP NINJAS</span>
                    <h3>ผู้นำแห่งแผ่นดินใหญ่</h3>
                  </div>
                  <button onClick={() => setTab('rank')}>
                    ดูอันดับทั้งหมด <span>↗</span>
                  </button>
                </div>
                {rankingLoading ? (
                  <p className="uc-small-empty">กำลังโหลดอันดับ…</p>
                ) : rankings?.rows.length ? (
                  <div className="uc-leader-list">
                    {rankings.rows.slice(0, 3).map((player) => (
                      <div key={player.accountId}>
                        <span className={`uc-leader-number medal-${player.rank}`}>0{player.rank}</span>
                        <img src={asset(`icons/people/${player.avatar}.png`)} alt="" />
                        <div>
                          <b>{player.displayName}</b>
                          <small>Lv. {player.level}</small>
                        </div>
                        <span>
                          {number(player.value)} <small>EXP</small>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="uc-small-empty">ทำเนียบนินจากำลังรอชื่อของคุณ</p>
                )}
              </div>
              <div className="uc-membership">
                <span className="uc-eyebrow">YOUR MEMBERSHIP</span>
                <h3>{vip ? 'สิทธิพิเศษสำหรับคุณ' : 'พร้อมไปให้ไกลกว่าเดิม'}</h3>
                <p>
                  {vip ? (svip?'สมาชิก SVIP · ส่วนลด 30%':'รับส่วนลดสินค้า VIP ในไอเท็มมอลล์') : 'เตรียมความพร้อมสำหรับทุกการผจญภัย เลือกดูไอเท็มและบัตร VIP ได้ที่ร้านค้า'}
                </p>
                {vip && character ? (
                  <div className="uc-membership-date">
                    {svip?(character.svipExpiresAt?`SVIP ถึง ${new Date(character.svipExpiresAt).toLocaleString('th-TH',{timeZone:'Asia/Bangkok'})}`:'SVIP · ไม่กำหนดวันหมดอายุ'):'VIP ถึง '}
                    {!svip&&new Date(character.vipExpiresAt).toLocaleDateString('th-TH', {
                      timeZone: 'Asia/Bangkok',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setCategory(categories.some(([id]) => id === 'vip') ? 'vip' : categories[0]?.[0] ?? '');
                      setTab('mall');
                    }}>
                    ดูบัตร VIP <Icon name="arrow" />
                  </button>
                )}
                <span className="uc-membership-seal" aria-hidden="true">
                  忍
                </span>
              </div>
            </section>
          </>
        )}
        {tab === 'rank' && (
          <section className="uc-section">
            <div className="uc-section-heading">
              <div>
                <span className="uc-eyebrow">{serverName} LEADERBOARD</span>
                <h2>ทำเนียบยอดนินจา</h2>
                <p>อันดับผู้เล่นจริงในเซิร์ฟเวอร์ {serverId} · อัปเดตทุก 30 วินาที</p>
              </div>
              <div className="uc-segment">
                <button className={metric === 'level' ? 'active' : ''} onClick={() => setMetric('level')}>
                  อันดับเลเวล
                </button>
                <button className={metric === 'arena' ? 'active' : ''} onClick={() => setMetric('arena')}>
                  อันดับอารีน่า
                </button>
              </div>
            </div>
            <div className="uc-village-filter"><label htmlFor="ranking-village">จัดอันดับตามหมู่บ้าน</label><select id="ranking-village" value={rankingVillage} onChange={e=>setRankingVillage(Number(e.target.value))}><option value={0}>ทุกหมู่บ้าน</option>{Object.entries(rankingVillages).map(([id,name])=><option key={id} value={id}>{name}</option>)}</select><span>{metric==='arena'?'เรียงตามคะแนนอารีน่า':'เรียงตามเลเวลและ EXP'}</span></div>
            {!rankingLoading && !!rankings?.villages?.length && <div className="uc-village-leaders" aria-label="อันดับหนึ่งแต่ละหมู่บ้าน">{rankings.villages.map(({village,total,leader})=><button key={village} className={rankingVillage===village?'active':''} onClick={()=>setRankingVillage(village)}><strong>{rankingVillages[village]}</strong>{leader?<><img src={asset(`icons/people/${leader.avatar}.png`)} alt=""/><b>{leader.displayName}</b><span>อันดับ 1 · Lv. {leader.level}</span><small>{number(leader.value)} {metric==='arena'?'คะแนน':'EXP'}</small></>:<span>ยังไม่มีผู้เล่น</span>}<small>ผู้เล่น {number(total)} คน</small></button>)}</div>}
            {!rankingLoading && rankings?.self && (
              <div className="uc-self-rank">
                อันดับของคุณ <b>#{rankings.self.rank}</b>
                <span>
                  {rankings.self.displayName} · Lv. {rankings.self.level}
                </span>
              </div>
            )}
            {rankingLoading ? (
              <div className="uc-empty">กำลังโหลดอันดับผู้เล่น…</div>
            ) : !rankings?.rows.length ? (
              <div className="uc-empty">ยังไม่มีตัวละครในอันดับ มาเป็นนินจาคนแรกกัน!</div>
            ) : (
              <>
                <div className="uc-podium">
                  {rankings.rows.slice(0, 3).map((player) => (
                    <div key={player.accountId} className={`place-${player.rank}`}>
                      <span className="uc-medal">#{player.rank}</span>
                      <img src={asset(`icons/people/${player.avatar}.png`)} alt="" />
                      <h3>{player.displayName}</h3>
                      <p>
                        Lv. {player.level} <span>·</span>{' '}
                        {metric === 'arena' ? `${number(player.value)} คะแนน` : `${number(player.value)} EXP`}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="uc-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>อันดับ</th>
                        <th>ผู้เล่น</th>
                        <th>หมู่บ้าน</th>
                        <th>เลเวล</th>
                        <th>{metric === 'arena' ? 'คะแนนอารีน่า' : 'EXP'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankings.rows.map((player) => (
                        <tr key={player.accountId} className={rankings.self?.accountId === player.accountId ? 'is-self' : ''}>
                          <td>#{player.rank}</td>
                          <td>{player.displayName}</td>
                          <td>{rankingVillages[player.village ?? 0] ?? '—'}</td>
                          <td>{player.level}</td>
                          <td>{number(player.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}
        {tab === 'topupRank' && <TopupLeaderboard request={request}/>}
        {tab === 'mall' && (
          <section className="uc-section">
            <div className="uc-section-heading">
              <div>
                <span className="uc-eyebrow">USERCENTER EXCLUSIVE SHOP</span>
                <h2>ไอเท็มมอลล์</h2>
                <p>ชำระด้วยโกลด์ · ส่งสินค้าเข้ากระเป๋าตัวละครเซิร์ฟเวอร์ {serverId} ทันที</p>
              </div>
              <span className="uc-mall-balance">◈ {number(profile?.currency.gold)} โกลด์</span>
            </div>
            {!mall?.canBuy && (
              <div className="uc-info">
                สร้างตัวละครในเซิร์ฟเวอร์ {serverId} ก่อนซื้อสินค้า{' '}
                <button onClick={()=>enter()} disabled={entering || !profile?.server.open}>
                  เข้าเกม →
                </button>
              </div>
            )}
            {mall && !mall.enabled && <div className="uc-info">ไอเท็มมอลล์ปิดให้บริการชั่วคราว</div>}
            <nav className="uc-categories" aria-label="หมวดสินค้า">
              {categories.map(([id, label]) => (
                <button key={id} className={category === id ? 'active' : ''} onClick={() => setCategory(id)}>
                  {label}
                  <span>{mall?.products.filter((product) => product.category === id).length ?? 0}</span>
                </button>
              ))}
            </nav>
            <div className="uc-shop-toolbar">
              <label className="uc-search">
                <Icon name="search" />
                <input
                  aria-label="ค้นหาไอเท็ม"
                  placeholder="ค้นหาชื่อหรือรหัสไอเท็มในหมวดนี้…"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </label>
              <select aria-label="เรียงสินค้า" value={sort} onChange={(event) => setSort(event.target.value)}>
                <option value="default">เรียงตามรายการแนะนำ</option>
                <option value="low">ราคา: ต่ำไปสูง</option>
                <option value="high">ราคา: สูงไปต่ำ</option>
                <option value="name">เรียงตามชื่อ</option>
              </select>
              <small>{products.length} รายการ</small>
            </div>
            {message && (
              <div className="uc-success" role="status">
                {message}
              </div>
            )}
            {!products.length ? (
              <div className="uc-empty">
                <span>
                  <Icon name={search ? 'search' : 'mall'} />
                </span>
                <h3>{search ? 'ไม่พบไอเท็มที่ค้นหา' : 'ยังไม่มีสินค้าในหมวดนี้'}</h3>
                <p>{search ? 'ลองใช้คำค้นอื่น หรือเลือกดูหมวดสินค้าเพิ่มเติม' : 'กลับมาเลือกไอเท็มสำหรับการผจญภัยของคุณได้เร็ว ๆ นี้'}</p>
                {search && <button onClick={() => setSearch('')}>ล้างคำค้น</button>}
              </div>
            ) : (
              <div className="uc-products">
                {products.map((product) => (
                  <article key={product.iid}>
                    <div className="uc-product-art">
                      <span className="uc-product-category">{categories.find(([id]) => id === product.category)?.[1]}</span>
                      {product.price !== product.basePrice && <span className="uc-product-vip">VIP −20%</span>}
                      <img src={asset(product.src)} alt="" />
                    </div>
                    <div className="uc-product-copy">
                      <small>ITEM #{product.iid}</small>
                      <h3>{product.name}</h3>
                      {product.price !== product.basePrice && (
                        <p className="uc-vip-price">
                          <del>{number(product.basePrice)} โกลด์</del>
                        </p>
                      )}
                      <div className="uc-product-price">
                        <strong>
                          <Icon name="coin" />
                          {number(product.price)} <small>โกลด์</small>
                        </strong>
                        <button disabled={busy || !mall?.canBuy || !mall.enabled} onClick={() => choose(product)}>
                          ซื้อ
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
        <footer className="uc-footer">
          PkNiN <span>ผจญภัยไปด้วยกันในโลกนินจา</span>
        </footer>
      </main>
      {selected && (
        <div className="uc-modal-backdrop">
          <section className="uc-purchase-modal" role="dialog" aria-modal="true" aria-label="ยืนยันซื้อสินค้า">
            <span className="uc-eyebrow">CONFIRM PURCHASE</span>
            <h2>{selected.name}</h2>
            <img src={asset(selected.src)} alt="" />
            <label>
              จำนวน
              <input
                aria-label="จำนวนสินค้า"
                type="number"
                min={1}
                max={99}
                disabled={busy || !!purchase.current}
                value={quantity}
                onChange={(event) => setQuantity(Math.min(99, Math.max(1, Number(event.target.value) || 1)))}
              />
            </label>
            <p className="uc-purchase-total">
              รวม <b>{number(total)} โกลด์</b>
            </p>
            <p className={affordable ? 'uc-after-balance' : 'uc-purchase-error'}>
              {affordable
                ? `ยอดเงินคงเหลือหลังซื้อ ${number((profile?.currency.gold ?? 0) - total)} โกลด์`
                : 'โกลด์ไม่เพียงพอสำหรับรายการนี้'}
            </p>
            <small>สินค้าจะถูกส่งเข้ากระเป๋าตัวละครเซิร์ฟเวอร์ {serverId}</small>
            {purchaseError && (
              <p className="uc-purchase-error" role="alert">
                {purchaseError}
              </p>
            )}
            <div>
              <button
                disabled={busy}
                onClick={() => {
                  setSelected(null);
                  purchase.current = undefined;
                }}>
                ยกเลิก
              </button>
              <button className="uc-primary" disabled={busy || (!affordable && !purchase.current)} onClick={buy}>
                {busy ? 'กำลังซื้อ…' : purchase.current ? 'ตรวจสอบ / ลองอีกครั้ง' : 'ยืนยันซื้อ'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>,
    document.body
  );
}
